import axios, { AxiosError, AxiosRequestConfig, Method } from 'axios';

type RequestOptions = {
  params?: unknown;
  includeAuthorization?: boolean;
  additionalHeaders?: Record<string, string>;
  responseType?: 'json' | 'blob';
};

const defaultRequestOptions: RequestOptions = {
  params: {},
  includeAuthorization: true,
  additionalHeaders: {},
  responseType: 'json',
};

export class HttpService {
  public static authMode: 'token' | 'invitationCode' = 'token';
  private static accessToken: string = '';
  private static invitationCode: string = '';

  private axiosInstance;

  constructor({ baseURL }: { baseURL: string }) {
    this.axiosInstance = axios.create({ baseURL });
    this.setupInterceptors();
  }

  setAccessToken(accessToken: string) {
    HttpService.accessToken = accessToken;
    HttpService.authMode = 'token';
  }

  setInvitationCode(invitationCode: string) {
    HttpService.invitationCode = invitationCode;
    HttpService.authMode = 'invitationCode';
  }

  async get(url: string, options: RequestOptions = defaultRequestOptions) {
    const response = this.request(
      url,
      'GET',
      null,
      options.params,
      options.responseType,
      options.includeAuthorization,
      options.additionalHeaders,
    );
    return response;
  }

  async post(
    url: string,
    data: unknown,
    options: RequestOptions = defaultRequestOptions,
  ) {
    const response = this.request(
      url,
      'POST',
      data,
      options.params,
      options.responseType,
      options.includeAuthorization,
      options.additionalHeaders,
    );
    return response;
  }

  async patch(
    url: string,
    data: unknown,
    options: RequestOptions = defaultRequestOptions,
  ) {
    const response = this.request(
      url,
      'PATCH',
      data,
      options.params,
      options.responseType,
      options.includeAuthorization,
      options.additionalHeaders,
    );
    return response;
  }

  async delete(url: string, options: RequestOptions = defaultRequestOptions) {
    const response = this.request(
      url,
      'DELETE',
      null,
      options.params,
      options.responseType,
      options.includeAuthorization,
      options.additionalHeaders,
    );
    return response;
  }

  private async request(
    url: string,
    method: Method,
    data?: unknown,
    params?: unknown,
    responseType: 'json' | 'blob' = 'json',
    includeAuthorization = true,
    additionalHeaders?: Record<string, string>,
  ) {
    const config: AxiosRequestConfig = {};
    config.url = url;
    config.method = method;
    config.data = data;
    config.params = params;
    config.headers = {};
    config.responseType = responseType;

    if (includeAuthorization) {
      if (HttpService.authMode === 'token') {
        config.headers = { Authorization: `Bearer ${HttpService.accessToken}` };
      } else if (HttpService.authMode === 'invitationCode') {
        config.headers = { 'x-invitation-code': HttpService.invitationCode };
      }
    }

    if (additionalHeaders) {
      config.headers = { ...config.headers, ...additionalHeaders };
    }

    const response = await this.axiosInstance.request(config);
    return response;
  }

  private setupInterceptors() {
    const onRequestError = (error: AxiosError): Promise<AxiosError> => {
      switch (error?.response?.status) {
        case 401:
          console.error('401 unauthorized => signout');
          if (HttpService.authMode === 'token') {
            window.location.href = '/logout';
          }
          break;
      }
      return Promise.reject(error);
    };
    this.axiosInstance.interceptors.response.use(null, onRequestError);
  }
}
